import type { HeicJob, HeicResult } from './heicWorker';

let worker: Worker | null = null;
let activeJobId: string | null = null;
let conversionQueue: Promise<void> = Promise.resolve();

// Check if we're in a browser environment
const isBrowser = typeof window !== 'undefined' && typeof Worker !== 'undefined';

const getWorker = (): Worker => {
  if (!isBrowser) {
    throw new Error('HEIC conversion requires a browser environment with Worker support');
  }
  if (worker) return worker;
  worker = new Worker(new URL('./heicWorker.ts', import.meta.url), { type: 'module' });
  return worker;
};

const convertHeicInWorker = (params: HeicJob): Promise<Blob> => {
  const w = getWorker();

  return new Promise((resolve, reject) => {
    const handler = (ev: MessageEvent<HeicResult>) => {
      const msg = ev.data;
      if (msg.jobId !== params.jobId) return;

      w.removeEventListener('message', handler);

      if (msg.ok === false) {
        reject(new Error(msg.error));
      } else {
        resolve(msg.blob);
      }
    };

    w.addEventListener('message', handler);
    w.postMessage(params, [params.bytes]);
  });
};

export const convertHeicSingleFlight = async (args: {
  imageId: string;
  bytes: ArrayBuffer;
  maxSize: number;
}): Promise<{ jobId: string; blob: Blob }> => {
  if (!isBrowser) {
    throw new Error('HEIC conversion requires a browser environment');
  }

  const jobId = crypto.randomUUID();
  activeJobId = jobId;

  const run = async () => {
    const blob = await convertHeicInWorker({ jobId, ...args });

    if (activeJobId !== jobId) {
      throw new Error('stale job');
    }

    return blob;
  };

  const queued = conversionQueue.then(run, run);
  conversionQueue = queued.then(() => undefined, () => undefined);

  const blob = await queued;
  return { jobId, blob };
};

export const cancelActiveHeicJob = (): void => {
  activeJobId = null;
};
